import { ChatMessage } from './aiAssistantModel'
import { ChatApiError } from './chatApiError'

function isNotFound(err: unknown): boolean {
  return err instanceof ChatApiError && err.status === 404
}

function throwIfAborted(signal?: AbortSignal): void {
  if (signal?.aborted) {
    throw Object.assign(new Error('The request was cancelled.'), { name: 'AbortError' })
  }
}

/**
 * Run a chat request, recovering once if the chat has expired (chat#274).
 *
 * The chat API forgets an idle chat after 30 minutes, but keeps a saved copy.
 * On a 404 this restores that copy and retries on the same session. If there
 * is no copy to restore and `startNew` is given, it starts a new chat and runs
 * there instead — only a send may do that, since an approval or form answer
 * means nothing outside its own chat. Resolves to the session the request
 * succeeded on; anything else is rethrown.
 *
 * Once `signal` is aborted it neither starts a new chat nor retries, so a
 * cancel during recovery leaves nothing behind.
 */
export async function runWithSessionRecovery(
  sessionId: string,
  run: (sessionId: string) => Promise<void>,
  restore: (sessionId: string) => Promise<void>,
  startNew?: () => Promise<string>,
  signal?: AbortSignal
): Promise<string> {
  try {
    await run(sessionId)
    return sessionId
  } catch (err) {
    if (!isNotFound(err)) {
      throw err
    }
  }

  try {
    await restore(sessionId)
  } catch (err) {
    if (!isNotFound(err) || !startNew) {
      throw err
    }
    throwIfAborted(signal)
    const newSessionId = await startNew()
    throwIfAborted(signal)
    await run(newSessionId)
    return newSessionId
  }

  throwIfAborted(signal)
  await run(sessionId)
  return sessionId
}

/**
 * Whether to switch to the chat a recovered request moved to: only when it
 * did move, and the user is still on the chat it started from.
 */
export function shouldAdoptSession(
  startedOn: string,
  ranOn: string,
  current: string | null
): boolean {
  return ranOn !== startedOn && current === startedOn
}

/**
 * The messages to keep on screen when a send moves to a new chat: the resent
 * message and its reply bubble. The new chat has none of the earlier turns.
 */
export function keepLastExchange(messages: ChatMessage[]): ChatMessage[] {
  return messages.slice(-2)
}

export const CHAT_EXPIRED_TEXT = 'This chat expired. Start a new one to continue.'

export const CHAT_RESTARTED_TEXT =
  'Your earlier chat expired, so this message started a new one.'

/** What to show the user for a failed chat request, never a raw status line. */
export function chatErrorText(err: unknown): string {
  if (!(err instanceof ChatApiError)) {
    return err instanceof Error ? err.message : String(err)
  }
  if (err.status === 404) {
    return CHAT_EXPIRED_TEXT
  }
  if (err.status === 401) {
    return 'Your sign-in has expired. Refresh the page and sign in again.'
  }
  if (err.status === 403) {
    return "You don't have access to this chat. Start a new one to continue."
  }
  if (err.status === 429) {
    return 'The assistant is busy. Wait a moment and try again.'
  }
  if (err.status >= 500) {
    return "The assistant isn't responding right now. Try again in a moment."
  }
  return 'Something went wrong. Try again.'
}
